// Check if text contains CJK characters (Japanese, Mandarin, Korean)
export function isCJKText(text) {
    // Japanese: Hiragana, Katakana, Kanji
    // Mandarin: Chinese characters
    // Korean: Hangul (but we want to treat it consistently)
    const cjkRegex = /[\u3040-\u309f\u30a0-\u30ff\u4e00-\u9fff\uac00-\ud7af]/;
    return cjkRegex.test(text);
}

export const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// Worker safeguard (frontend-side): sometimes the LLM "bleeds" into another character's turn
// (e.g. the response for character A ends with "\n\n**CharacterB** ..." or a dangling "CharacterB").
// We deterministically trim at common next-speaker markers and remove trailing stubs.
export const sanitizeCrossCharacterBleed = (rawText, currentCharacterId, activeCharacters = []) => {
    const text = typeof rawText === 'string' ? rawText : '';
    if (!text) return '';

    const currentId = String(currentCharacterId ?? '');
    const otherNames = (Array.isArray(activeCharacters) ? activeCharacters : [])
        .filter(c => String(c?.id ?? c?.characterId ?? '') !== currentId)
        .map(c => String(c?.name ?? c?.characterName ?? '').trim())
        .filter(Boolean);

    if (otherNames.length === 0) return text;

    let cutIndex = null;

    const considerCut = (idx) => {
        if (idx === -1) return;
        if (cutIndex === null || idx < cutIndex) cutIndex = idx;
    };

    for (const name of otherNames) {
        const escapedName = escapeRegExp(name);

        // Strong markers: start of a new paragraph/line with explicit speaker label
        const markerRegexes = [
            new RegExp(`(^|\\n\\n|\\n)\\*\\*\\s*${escapedName}\\s*\\*\\*`, 'i'),
            new RegExp(`(^|\\n\\n|\\n)${escapedName}\\s*:`, 'i'),
            // Common drift pattern: new paragraph starts describing another character's actions
            // e.g. "\n\nMakima's smirk..." or "\n\nMakima’s smirk..."
            new RegExp(`(^|\\n\\n|\\n)${escapedName}(?:'s|’s)\\b`, 'i'),
            // Catch "\n\nFrieren adjusts..." style drift (no colon/possessive)
            new RegExp(`(^|\\n\\n|\\n)${escapedName}\\b`, 'i')
        ];

        for (const rx of markerRegexes) {
            const m = rx.exec(text);
            if (m && typeof m.index === 'number') {
                // Cut at the boundary (after the newline group, if present)
                considerCut(m.index + (m[1] ? m[1].length : 0));
            }
        }

        // Weak markers: incomplete markdown / dangling speaker starts (no closing ** yet)
        considerCut(text.indexOf(`\n\n**${name}`));
        considerCut(text.indexOf(`\n**${name}`));
        considerCut(text.indexOf(`\n\n${name}\n`));
        considerCut(text.indexOf(`\n${name}\n`));
    }

    let out = cutIndex === null ? text : text.slice(0, cutIndex);
    out = out.trimEnd();

    // Remove trailing "stub" lines like: "**Makima" or "Revy" that indicate the next speaker started.
    const maxStubRemovals = 3;
    for (let i = 0; i < maxStubRemovals; i++) {
        const trimmed = out.trimEnd();
        const lines = trimmed.split('\n');
        const lastLineRaw = (lines[lines.length - 1] ?? '').trim();
        if (!lastLineRaw) break;

        const isStub = otherNames.some((n) => {
            const ln = lastLineRaw.toLowerCase();
            const nn = String(n).toLowerCase();
            if (ln === nn) return true;
            if (ln === `**${nn}`) return true;
            if (ln === `**${nn}**`) return true;
            if (ln.startsWith(`**${nn}`)) return true; // partial "**Name"
            return false;
        });

        if (!isStub) break;

        lines.pop();
        out = lines.join('\n').trimEnd();
    }

    return out.trim();
};
